import React, { useState } from 'react';
import { Head } from 'vite-react-ssg';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WaitlistDialog } from '@/components/WaitlistDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import RelatedLinks from '@/components/RelatedLinks';
import Breadcrumbs from '@/components/Breadcrumbs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const WhatIsDeepseekAI = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const faqItems = [
    {
      question: "What is DeepSeek AI?",
      answer: "DeepSeek AI is an open-source artificial intelligence company founded in July 2023 by Liang Wenfeng in Hangzhou, China. It develops advanced AI models including DeepSeek-V3 and DeepSeek-R1, known for exceptional reasoning capabilities and cost-effective training methods."
    },
    {
      question: "Is DeepSeek free to use?",
      answer: "Yes, DeepSeek offers free access to its models. The API has generous free tiers, and since the models are open-source under the MIT license, you can download and run them locally for free with no restrictions."
    },
    {
      question: "How does DeepSeek compare to ChatGPT?",
      answer: "DeepSeek R1 competes with GPT-4 on reasoning and coding benchmarks, often at lower cost. DeepSeek is open-source while ChatGPT is proprietary. DeepSeek excels in math and coding; ChatGPT has broader general knowledge and features."
    },
    {
      question: "What makes DeepSeek different from other AI models?",
      answer: "DeepSeek stands out for its cost-efficient training (V3 cost only $6M vs $100M+ for competitors), open-source approach, visible chain-of-thought reasoning in R1, and advanced techniques like Mixture-of-Experts (MoE) architecture."
    },
    {
      question: "Is DeepSeek safe to use?",
      answer: "DeepSeek AI is generally safe for most uses. However, it's subject to Chinese regulations and data may be processed in China. For sensitive applications, consider privacy implications. The open-source versions can run locally for full data control."
    },
    {
      question: "Can I run DeepSeek on my computer?",
      answer: "Yes, DeepSeek models are open-source. Smaller distilled versions can run on consumer hardware with 16GB+ VRAM. Full models require more powerful systems. Tools like Ollama and llama.cpp make local deployment easier."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "What is DeepSeek AI? Understanding DeepSeek.com's Groundbreaking Open-Source Revolution",
    "description": "Discover how DeepSeek.com (Hangzhou DeepSeek Artificial Intelligence Co., Ltd.) is challenging Silicon Valley's AI dominance with cost-effective, open-source models through our fan site perspective.",
    "image": "https://deepseek.ai/og-image.png",
    "datePublished": "2025-04-03T08:00:00+00:00",
    "dateModified": "2025-12-21T08:00:00+00:00",
    "author": {
      "@type": "Organization", 
      "name": "The Independent DeepSeek Guide",
      "url": "https://deepseek.ai"
    }
  };

  return (
    <>
      <Head>
        <title>What Is DeepSeek AI? Open-Source Model Guide</title>
        <meta name="description" content="Learn what DeepSeek AI is, how its open-source models work, and why this independent fan site covers its cost-efficient AI technology." />
        <meta name="keywords" content="DeepSeek.com, Hangzhou DeepSeek, China AI, open-source AI, AI revolution, DeepSeek-V3, AI cost efficiency, DeepSeek vs ChatGPT" />
        
        {/* Open Graph */}
        <meta property="og:title" content="What Is DeepSeek AI? Open-Source Model Guide" />
        <meta property="og:description" content="Learn what DeepSeek AI is, how its open-source models work, and why this independent fan site covers its cost-efficient AI technology." />
        <meta property="og:url" content="https://deepseek.ai/blog/what-is-deepseek-ai" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What Is DeepSeek AI? Open-Source Model Guide" />
        <meta name="twitter:description" content="Learn what DeepSeek AI is, how its open-source models work, and why this independent fan site covers its cost-efficient AI technology." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        
        {/* Canonical - ensuring it's accessible at the blog URL */}
        <link rel="canonical" href="https://deepseek.ai/blog/what-is-deepseek-ai" />
        
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/30 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
          <Breadcrumbs 
            items={[{ label: "Resources", path: "/blog" }]} 
            currentPage="What is DeepSeek AI?" 
          />
          
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-6 hover:bg-gray-100 group transition-all duration-300"
          >
            <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Back to Blog
          </Button>
          
          {/* Top navigation buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10 animate-fadeIn">
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
            >
              Download DeepSeek AI for Free!
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </a>
            <button
              onClick={() => setIsDialogOpen(true)}
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#0066FF] text-base font-medium rounded-full text-[#0066FF] bg-white hover:bg-blue-50 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
            >
              Create AI Agents
              <Bot className="ml-2 h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <article className="prose prose-lg max-w-none bg-white rounded-xl shadow-sm p-8 border border-gray-100">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>Disclaimer:</strong> This is an independent fan site. We are not affiliated with, sponsored by, or endorsed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd. For official information, visit <a href="https://www.deepseek.com" target="_blank" rel="noopener noreferrer" className="underline">DeepSeek.com</a>.
              </p>
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">What is DeepSeek AI? Understanding DeepSeek.com's Groundbreaking Open-Source Revolution</h1>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-r-md">
              <p className="text-lg text-gray-800">DeepSeek.com is turning heads across the global technology landscape. Hailed as a "Sputnik moment" in artificial intelligence, the company developed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd. represents a bold new approach—one that challenges the high-cost, closed-source models dominating Silicon Valley while offering an accessible, efficient, and open alternative. In this blog post, we'll dive into what DeepSeek.com is, how it works, and why it's creating both excitement and debate in the tech industry.</p>
            </div>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">A New Contender in the AI Arena</h2>

            <p>DeepSeek.com is developed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd., a Chinese artificial intelligence research company founded in July 2023 by Liang Wenfeng, a hedge fund entrepreneur with a passion for technology and innovation. Headquartered in Hangzhou, Zhejiang, DeepSeek is owned and funded by the Chinese hedge fund High-Flyer. Unlike many Western AI companies that keep their training data and model architectures proprietary, DeepSeek embraces an open-source philosophy—releasing its models with open weights under the MIT license. This means that anyone can download, use, and modify the model code, opening the door for widespread collaboration and rapid innovation.<br />
            citeturn0search4</p>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">The Technology Behind DeepSeek</h2>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Cost-Efficiency Through Innovation</h3>

            <p>One of DeepSeek.com's most notable achievements is its ability to deliver state-of-the-art AI performance at a fraction of the cost. For example, DeepSeek claims that training its flagship model—DeepSeek-V3—cost only about US$6 million, compared to the US$100 million or more often spent by competitors such as OpenAI on models like GPT-4. This dramatic cost reduction is made possible by leveraging innovative architectures and optimization techniques, including:</p>
            
            <ul className="space-y-2 mt-4 mb-6">
              <li className="flex items-start">
                <span className="font-bold mr-2">Mixture-of-Experts (MoE):</span> 
                <span>DeepSeek's models selectively activate only a subset of parameters (e.g., 37 billion out of 671 billion total in V3) for each token, greatly reducing computational demands.</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">Multi-Head Latent Attention (MLA):</span> 
                <span>This novel approach compresses the model's key-value cache into a latent vector, enabling efficient inference without sacrificing performance.</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2">Reinforcement Learning (RL) for Reasoning:</span> 
                <span>With its R1 series, DeepSeek pioneers reinforcement learning techniques to enhance reasoning capabilities without the extensive cost of supervised fine-tuning.</span>
              </li>
            </ul>
            <p>citeturn0academia45<br />
            citeturn0academia48</p>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Open-Source Advantages</h3>

            <p>By releasing its AI models as open source, DeepSeek.com democratizes access to high-performance language models. Developers around the world can experiment with, modify, and improve these models, accelerating the pace of AI innovation. This open-weight strategy contrasts sharply with the "black-box" approaches of many US-based models, where underlying architectures and training methodologies remain hidden from public view.<br />
            citeturn0search16</p>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">DeepSeek's Competitive Edge</h2>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Disrupting Silicon Valley's Business Model</h3>

            <p>DeepSeek.com's breakthrough has sent shockwaves through the tech industry. Its cost-effectiveness has not only spurred excitement among developers but also rattled established market players. For instance, major tech stocks—most notably those of Nvidia—tumbled after DeepSeek's emergence, as investors questioned the long-held belief that state-of-the-art AI requires massive capital investments. Silicon Valley insiders now face a critical challenge: justify the premium pricing of their proprietary models when an open-source alternative is available at a fraction of the cost.<br />
            citeturn0news50</p>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">A "Sputnik Moment" for AI</h3>

            <p>Venture capitalist Marc Andreessen famously described DeepSeek.com's impact as an "AI Sputnik moment." Just as the launch of Sputnik in 1957 signaled a new era in space exploration and spurred the US into action, DeepSeek's rapid advancements hint at a future where resource limitations no longer stifle breakthrough AI innovations. This has led to intense debate in both industry and policy circles about the implications for global AI leadership and the effectiveness of existing export controls on advanced semiconductors.<br />
            citeturn0news52</p>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Integration and Adoption</h3>

            <p>Despite initial concerns over its open-source nature and censorship aligned with Chinese regulations, DeepSeek.com has gained traction across the tech ecosystem. Its models have been integrated into various platforms—from startups to major cloud service providers like Microsoft Azure and Amazon AWS. Companies are drawn by the prospect of drastically reduced operational costs and the flexibility to tailor models to their specific needs.<br />
            citeturn0news49<br />
            citeturn0news53</p>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Market Impact & Global Reaction</h2>

            <div className="grid md:grid-cols-2 gap-6 mt-6 mb-8">
              <Card className="border border-gray-200 hover:shadow-md transition-all">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">Wall Street and Silicon Valley Shake-Up</h3>
                  <p>DeepSeek's disruptive entry into the market has led to dramatic shifts in investor sentiment. On the day its chatbot application soared to the top of Apple's App Store in the United States, shares of Nvidia and other tech giants experienced sharp declines, erasing billions in market value. Analysts are now re-evaluating the long-term business models built around expensive, closed-source AI infrastructures.<br />
                  citeturn0news51</p>
                </CardContent>
              </Card>
              
              <Card className="border border-gray-200 hover:shadow-md transition-all">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">Geopolitical Implications</h3>
                  <p>The rise of DeepSeek AI also underscores broader geopolitical dynamics. U.S. export controls on advanced semiconductors—intended to hinder China's AI progress—have inadvertently pushed Chinese researchers to innovate with less powerful hardware (like Nvidia's H800 chips). This adaptation not only proves the resilience of China's tech ecosystem but also challenges the notion that high-end chips are the only path to cutting-edge AI performance. Such developments are prompting policymakers on both sides of the Pacific to reconsider strategies and alliances in the AI domain.<br />
                  citeturn0news52</p>
                </CardContent>
              </Card>
            </div>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Open-Source, Censorship, and Ethical Considerations</h2>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">The Open-Source Promise</h3>

            <p>For many, the greatest allure of DeepSeek.com is its open-source model. By lowering the barriers to entry, DeepSeek encourages a more collaborative and innovative AI landscape. This could foster a future where AI advancements are not monopolized by a handful of billion-dollar corporations, but are accessible to startups, researchers, and even individual enthusiasts. The potential for accelerated innovation is immense, and it aligns with the early ideals of organizations like the original OpenAI.<br />
            citeturn0news54</p>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Censorship and Data Security Concerns</h3>

            <p>However, the openness comes with its share of challenges. DeepSeek.com's models are subject to self-censorship to comply with Chinese regulations, meaning that sensitive topics such as the Tiananmen Square massacre, human rights issues, or questions about Taiwan may be automatically filtered out or sanitized. For businesses and developers outside China, this raises questions about data security and content neutrality. Critics worry that such censorship could limit the model's usefulness in global applications and may even pose risks to privacy and freedom of information.<br />
            citeturn0news51<br />
            citeturn0news54</p>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Why DeepSeek AI Could Be a Game Changer for Your Business</h2>

            <div className="bg-gray-50 p-6 rounded-lg my-6">
              <p className="mb-4">For businesses looking to harness the power of AI without breaking the bank, DeepSeek.com offers a compelling proposition. Here are a few reasons why you might consider integrating DeepSeek into your operations:</p>

              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <span className="font-semibold">Cost Savings:</span> With training costs estimated at a mere fraction of those for comparable US models, DeepSeek.com can drastically reduce your AI "utility bills."
                  </div>
                </li>
                <li className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <span className="font-semibold">Customization:</span> The open-source nature of DeepSeek.com allows you to modify and optimize the model for your specific needs, be it customer service, coding assistance, or data analysis.
                  </div>
                </li>
                <li className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div>
                    <span className="font-semibold">Rapid Innovation:</span> As more developers and companies adopt and improve upon DeepSeek.com's models, you can benefit from a continuously evolving ecosystem that keeps pace with the latest advancements.
                  </div>
                </li>
                <li className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold">4</span>
                  </div>
                  <div>
                    <span className="font-semibold">Competitive Edge:</span> Integrating a cost-effective and powerful AI solution could give your business a strategic advantage, especially in industries where rapid data processing and automation are key.
                  </div>
                </li>
                <li className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold">5</span>
                  </div>
                  <div>
                    <span className="font-semibold">Global Perspective:</span> While concerns remain about censorship, many companies are finding ways to "Americanise" or localize the output to suit diverse market needs.
                  </div>
                </li>
              </ul>
              <p className="mt-4">citeturn0news49<br />
              citeturn0news53</p>
            </div>

            <Separator className="my-8" />

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Conclusion</h2>

            <p>DeepSeek.com is more than just another chatbot—it's a paradigm shift in the way advanced AI models are built, deployed, and accessed. By breaking the mold of expensive, proprietary systems, Hangzhou DeepSeek Artificial Intelligence Co., Ltd. is democratizing AI and sparking a global debate on cost, accessibility, and innovation in the field. Whether you're a developer, a business leader, or simply an AI enthusiast, keeping an eye on DeepSeek.com is essential as it continues to influence both technological and geopolitical landscapes.</p>

            <p>As the tech industry evolves and the boundaries of AI innovation expand, DeepSeek.com stands as a testament to how resourcefulness and open collaboration can challenge established norms—and perhaps, redefine the future of artificial intelligence.</p>

            <Separator className="my-8" />

            {/* Comparison Table */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">DeepSeek vs Other AI Models</h2>
            <div className="overflow-x-auto my-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-center">DeepSeek</TableHead>
                    <TableHead className="text-center">ChatGPT</TableHead>
                    <TableHead className="text-center">Claude</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Open Source</TableCell>
                    <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Chain-of-Thought</TableCell>
                    <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                    <TableCell className="text-center">Hidden</TableCell>
                    <TableCell className="text-center">Hidden</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">API Cost</TableCell>
                    <TableCell className="text-center">Very Low</TableCell>
                    <TableCell className="text-center">High</TableCell>
                    <TableCell className="text-center">High</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Local Deployment</TableCell>
                    <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              <Link to="/deepseek-vs-chatgpt" className="text-primary hover:underline">See full DeepSeek vs ChatGPT comparison</Link> | 
              <Link to="/deepseek-vs-claude" className="text-primary hover:underline ml-2">DeepSeek vs Claude</Link>
            </p>

            <Separator className="my-8" />

            {/* FAQ Section */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent>{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Separator className="my-8" />

            <p><em>References available upon request.</em></p>

            <div className="mt-12 mb-12 border-t border-b border-gray-200 py-8">
              <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
              <RelatedLinks />
            </div>
          </article>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
            >
              Try DeepSeek AI Now
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </a>
            <button
              onClick={() => setIsDialogOpen(true)}
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#0066FF] text-base font-medium rounded-full text-[#0066FF] bg-white hover:bg-blue-50 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
            >
              Get Updates
              <Bot className="ml-2 h-5 w-5" aria-hidden="true" />
            </button>
          </div>
            
          <div className="mt-12 text-center text-gray-500">
            <p>Last updated: April 3, 2025</p>
          </div>
        </div>
      </div>

      <WaitlistDialog 
        isOpen={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)} 
      />
    </>
  );
};

export default WhatIsDeepseekAI;
